<template>
  <div class="sidebar">
    <focus-trap :active="open">
      <nav id="sidebarMenu"
           tabindex="-1"
           class="sidebar-menu"
           ref="sidebarMenu"
           :aria-hidden="!open"
           aria-labelledby="sidebarMenuButton"
           role="menu"
           :class="{'open': open, 'ios-pwa': isIosPwa}">
        <div id="sidebarMenuParent">
          <b-row no-gutters>
            <b-col>
              <b-button id="sidebar-close-button"
                        class="button--icon"
                        role="menuitem"
                        label="Close Menu"
                        @click="closeSidebar">
                <inline-svg src="XCloseIcon"
                            class="closebtn">
                </inline-svg>
              </b-button>
            </b-col>
          </b-row>
          <div class="sidebar-bottom-spacing">
            <cash-1-loans-sidebar v-if="showCash1LoansSidebar"></cash-1-loans-sidebar>
            <california-check-cash-sidebar v-if="showCaliforniaCheckCashSidebar"></california-check-cash-sidebar>
            <check-into-cash-sidebar v-if="showCheckIntoCashSidebar"></check-into-cash-sidebar>
            <check-smart-sidebar v-if="showCheckSmartSidebar"></check-smart-sidebar>
            <community-choice-sidebar v-if="showCommunityChoiceSidebar"></community-choice-sidebar>
            <easy-money-sidebar v-if="showEasyMoneySidebar"></easy-money-sidebar>
            <debug-actions></debug-actions>
            <instaloan-sidebar v-if="showInstaLoanSidebar"></instaloan-sidebar>
            <referral-sidebar v-if="showReferralSidebar"></referral-sidebar>
            <titlemax-sidebar v-if="showTitlemaxSidebar"></titlemax-sidebar>
          </div>
        </div>
      </nav>
    </focus-trap>
  </div>
</template>

<script>
// Elements
import Cash1LoansSidebar from '../../loans/elements/c1l/Cash1LoansSidebar.vue'
import CaliforniaCheckCashSidebar from '../../loans/elements/ccc/CaliforniaCheckCashSidebar.vue'
import CheckIntoCashSidebar from '../../loans/elements/cic/CheckIntoCashSidebar.vue'
import CheckSmartSidebar from '../../loans/elements/chs/CheckSmartSidebar.vue'
import CommunityChoiceSidebar from '../../loans/elements/ccf/CommunityChoiceSidebar.vue'
import DebugActions from './DebugActions.vue'
import EasyMoneySidebar from '../../loans/elements/esm/EasyMoneySidebar.vue'
import InlineSvg from './InlineSvg.vue'
import InstaloanSidebar from '../../loans/elements/inl/InstaloanSidebar.vue'
import ReferralSidebar from '../../referral/elements/ReferralSidebar.vue'
import TitlemaxSidebar from '../../loans/elements/tmx/TitlemaxSidebar.vue'

// Libraries
import { FocusTrap } from 'focus-trap-vue'
import { mapGetters, mapState } from 'vuex'

// Utilities
import logger from '../../../utility/loggerUtil'
import router from '../../../utility/routerUtil'

import { BrandEnum, BrowserType } from '../../../constants.js'

export default {
  name: 'sidebar',

  components: {
    Cash1LoansSidebar,
    CaliforniaCheckCashSidebar,
    CheckIntoCashSidebar,
    CheckSmartSidebar,
    CommunityChoiceSidebar,
    DebugActions,
    EasyMoneySidebar,
    FocusTrap,
    InlineSvg,
    InstaloanSidebar,
    TitlemaxSidebar,
    ReferralSidebar
  },

  data () {
    return {
      serviceCallError: ''
    }
  },

  computed: {
    open () {
      return this.sidebarOpen
    },

    showInstaLoanSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.InstaLoan && !this.showReferralSidebar
    },

    showTitlemaxSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.TitleMax && !this.showReferralSidebar
    },

    showCash1LoansSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.Cash1Loans && !this.showReferralSidebar
    },

    showCaliforniaCheckCashSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.CaliforniaCheckCash && !this.showReferralSidebar
    },

    showCheckIntoCashSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.CheckIntoCash && !this.showReferralSidebar
    },

    showCheckSmartSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.CheckSmart && !this.showReferralSidebar
    },

    showCommunityChoiceSidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.CommunityChoice && !this.showReferralSidebar
    },

    showEasyMoneySidebar () {
      return this.open && router.getCurrentRouteBrandEnum(this.$route) === BrandEnum.EasyMoney && !this.showReferralSidebar
    },

    showReferralSidebar () {
      return this.isNonCustomer && this.isReferralRewardsUser && this.authenticated
    },

    isIosPwa () {
      return this.browserType === BrowserType.Safari && this.isPwa
    },

    ...mapGetters('referral', ['isReferralRewardsUser']),
    ...mapGetters('customer', ['isNonCustomer']),
    ...mapState('display', ['browserType', 'isPwa', 'sidebarOpen']),
    ...mapState('session', ['authenticated'])
  },

  methods: {
    closeSidebar () {
      this.$store.commit('display/closeSidebar')
    }
  },

  watch: {
    open (open) {
      if (open) {
        try {
          document.getElementById('sidebarMenu').focus()
        } catch (e) {
          logger.error('Unable to focus')
          logger.debug(e)
        }
      }
    }
  }
}
</script>

<style lang="scss">
@use '../../../assets/styles/colors';

.sidebar-menu {
  width: 0;
  height: 100vh;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  transition: width 0.25s ease;
}

.sidebar-menu.ios-pwa {
  padding-top: 1.25rem; /* Adding padding for iOS status bar */
}

.sidebar-card-image {
  margin-top: 2.25rem;
  text-align: center;
}

.sidebar-card-image img {
  height: 4.94rem;
  border-radius: 4px;
}

#sidebarMenuParent {
  min-height: calc(100% - calc(constant(safe-area-inset-bottom) + 42px)); /* iOS 11.0 */
  min-height: calc(100% - calc(env(safe-area-inset-bottom) + 42px)); /* iOS 11.2 */
}

.sidebar-menu.open {
  width: 18.62rem;
}

.sidebar-menu #sidebar-close-button,
.sidebar-menu .menu-row,
.sidebar-menu .version-row {
  padding-left: constant(safe-area-inset-left); /* iOS 11.0 */
  padding-left: env(safe-area-inset-left); /* iOS 11.2 */
}

.sidebar-menu .version-row {
  padding-left: constant(safe-area-inset-bottom); /* iOS 11.0 */
  padding-left: env(safe-area-inset-bottom); /* iOS 11.2 */
}

.sidebar-menu .closebtn {
  width: 1.375rem !important;
  height: 1.375rem !important;
  margin-top: 0.688rem;
  margin-bottom: 0.688rem;
  margin-left: 0.875rem;
  cursor: pointer;
}

.sidebar-bottom-spacing {
  margin-bottom: 4rem;
}

.menu-svg {
  width: 1.56rem;
  min-width: 1.56rem;
  max-width: 1.56rem;
  height: 1.56rem;
  min-height: 1.56rem;
  max-height: 1.56rem;
  margin-right: 0.94rem;
  margin-left: 0.94rem;
}

.menu-option {
  width: 100%;
  height: 3.625rem;
  padding: 0.313rem;
  white-space: nowrap !important;
  background: transparent !important;
  border-style: solid;
  border-width: 0 0 1px 0 !important;
  border-radius: 0 !important;
  outline: none !important;
  box-shadow: none !important;
  transition: 0s all;
}

/*stylelint-disable*/
.c1l {
  .sidebar-menu {
    background-color: colors.$white;
    box-shadow: 0 2px 10px 0 colors.$black-25op;

    .closebtn {
      .svg-fill {
        fill: colors.$c1l--blue-3;
      }
    }

    .menu-option {
      border-color: colors.$gray-300;

      .svg-stroke {
        stroke: colors.$c1l--blue-3;
      }

      .svg-fill {
        fill: colors.$c1l--blue-3;
      }

      &:hover,
      &:active,
      &:focus {
        color: colors.$white !important;
        background: colors.$c1l--green-1 !important;
        border-color: colors.$c1l--green-1 !important;

        .svg-stroke {
          stroke: colors.$white;
        }

        .svg-fill {
          fill: colors.$white;
        }
      }
    }
  }
}

.ccc {
  .sidebar-menu {
    background-color: colors.$white;
    box-shadow: 0 2px 10px 0 colors.$black-25op;

    .closebtn {
      .svg-fill {
        fill: colors.$ccc--blue-2;
      }
    }

    .menu-option {
      border-color: colors.$gray-300;

      .svg-stroke {
        stroke: colors.$ccc--blue-2;
      }

      .svg-fill {
        fill: colors.$ccc--blue-2;
      }

      &:hover,
      &:active,
      &:focus {
        color: colors.$white !important;
        background: colors.$ccc--blue-2 !important;
        border-color: colors.$ccc--blue-2 !important;

        .svg-stroke {
          stroke: colors.$white;
        }

        .svg-fill {
          fill: colors.$white;
        }
      }
    }
  }
}

.ccf {
  .sidebar-menu {
    background-color: colors.$white;
    box-shadow: 0 2px 10px 0 colors.$ccf--navy-4;

    .closebtn {
      .svg-fill {
        fill: colors.$ccf--navy-4;
      }
    }

    .menu-option {
      border-color: colors.$gray-300;

      .svg-stroke {
        stroke: colors.$ccf--navy-4 !important;
      }

      .svg-fill {
        fill: colors.$ccf--navy-4 !important;
      }

      &:hover,
      &:active,
      &:focus {
        fill: colors.$ccf--navy-4 !important;
        color: colors.$ccf--navy-4 !important;
        border-color: colors.$gray-200 !important;;
        background: colors.$ccf--navy-1 !important;
      }
    }
  }
}

.chs {
  .sidebar-menu {
    background-color: colors.$white;
    box-shadow: 0 2px 10px 0 colors.$chs--green-1;

    .closebtn {
      .svg-fill {
        fill: colors.$chs--orange-2;
      }
    }

    .menu-option {
      border-color: colors.$gray-300;

      .svg-stroke {
        stroke: colors.$chs--orange-2 !important;
      }

      .svg-fill {
        fill: colors.$chs--orange-2 !important;
      }

      &:hover,
      &:active,
      &:focus {
        color: colors.$chs--green-1 !important;
        background: colors.$chs--yellow-3 !important;
      }
    }
  }
}

.cic {
  .sidebar-menu {
    background-color: colors.$white;
    box-shadow: 0 2px 10px 0 colors.$cic--green-1;

    .closebtn {
      .svg-fill {
        fill: colors.$cic--green-1;
      }
    }

    .menu-option {
      border-color: colors.$gray-300;

      .svg-stroke {
        stroke: colors.$cic--green-1 !important;
      }

      .svg-fill {
        fill: colors.$cic--green-1 !important;
      }

      &:hover,
      &:active,
      &:focus {
        color: colors.$cic--green-1 !important;
        background: colors.$cic--green-4-20op !important;
      }
    }
  }
}

.esm {
  .sidebar-menu {
    background-color: colors.$white;
    box-shadow: 0 2px 10px 0 colors.$black-25op;

    .closebtn {
      .svg-fill {
        fill: colors.$esm--orange-2;
      }
    }

    .menu-option {
      border-color: colors.$gray-300;

      .svg-stroke {
        stroke: colors.$esm--orange-2 !important;
      }

      .svg-fill {
        fill: colors.$esm--orange-2 !important;
      }

      &:hover,
      &:active,
      &:focus {
        color: colors.$esm--green-3 !important;
        background: colors.$esm--green-1 !important;
      }
    }
  }
}

.inl {
  .sidebar-menu {
    background-color: colors.$inl--green-2;
    box-shadow: 3px 2px 8px 0 rgba(5, 11, 4, 0.6);

    .version-row {
      span {
        color: colors.$white !important;
      }
    }

    .closebtn {
      .svg-fill {
        fill: colors.$white;
      }
    }

    .menu-option {
      color: colors.$white !important;
      border-color: colors.$gray-300;

      .svg-stroke {
        stroke: colors.$white !important;
      }

      .svg-fill {
        fill: colors.$white !important;
      }

      &:hover,
      &:active,
      &:focus {
        color: colors.$black !important;
        background: colors.$inl--yellow-1 !important;
        border-color: colors.$inl--yellow-1 !important;
        .svg-stroke {
          stroke: colors.$black !important;
        }
        .svg-fill {
          fill: colors.$black !important;
        }
      }
    }
  }
}

.tmx {
  .sidebar-menu {
    background-color: colors.$white;
    box-shadow: 0 2px 10px 0 colors.$tmx--blue-1;

    .menu-option {
      border-color: colors.$gray-300;

      .svg-stroke {
        stroke: colors.$tmx--blue-1 !important;
      }

      .svg-fill {
        fill: colors.$tmx--blue-1 !important;
      }

      &:hover,
      &:active,
      &:focus {
        color: colors.$white !important;
        background: colors.$tmx--blue-1 !important;
        border-color: colors.$tmx--blue-1 !important;

        .svg-stroke {
          stroke: colors.$white !important;
        }

        .svg-fill {
          fill: colors.$white !important;
        }
      }
    }
  }
}
/*stylelint-enable*/
</style>
